import { delay, fork, select } from "redux-saga/effects";
import { call, put } from "redux-saga/effects";
import { selectUser } from "../../features/auth/authSlice";
import { User } from "../../types/User";
import { Automation } from "../../types/Automation";
import {
  setAutoLines,
  setAutomationOutput,
  setAutomations,
  setError as setDashError,
  setStatus as setDashStatus,
} from "../../features/library/librarySlice";
import * as actions from "../actions";

import {
  apiCreateAutomation,
  apiGetAutomations,
  apiRunAutomation,
  apiUpdateAutomation,
} from "../../services/AutomationAPI";
import {
  apiCreateAutoLine,
  apiGetAutoLines,
  apiUpdateAutoLine,
} from "../../services/AutoLineAPI";

export function* createAutomation(
  action: ReturnType<typeof actions.createAutomation>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const createResponse = yield call(
      apiCreateAutomation,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload
    );
  
    yield put(actions.getAutomations());

  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* getAutomations(
  action: ReturnType<typeof actions.getAutomations>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      apiGetAutomations,
      userState.default_organization_id,
      userState.default_workspace_id
    );

    yield put(setAutomations(response.data.automations));
  
    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* updateAutomation(
  action: ReturnType<typeof actions.updateAutomation>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const updateResponse = yield call(
      apiUpdateAutomation,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.automationId,
      action.payload.automationUpdateRequest
    );
  
    yield put(actions.getAutomations());

  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* createAutoLine(
  action: ReturnType<typeof actions.createAutoLine>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const createResponse = yield call(
      apiCreateAutoLine,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload.automation_id,
      action.payload
    );
  
    yield put(actions.getAutoLines(action.payload.automation_id));

  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* getAutoLines(
  action: ReturnType<typeof actions.getAutoLines>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      apiGetAutoLines,
      userState.default_organization_id,
      userState.default_workspace_id,
      action.payload
    );

    yield put(setAutoLines(response.data.auto_lines || response.data.AutoLines || []));

    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* updateAutoLine(
  action: ReturnType<typeof actions.updateAutoLine>
) {
  yield put(setDashStatus({ status: "loading", error: undefined }));

  try {
    const userState = (yield select(selectUser)) as User;
    yield call(
      () =>
        apiUpdateAutoLine(
          userState.default_organization_id,
          userState.default_workspace_id,
          action.payload.automationId,
          action.payload.autoLineId,
          action.payload.autoLineUpdateRequest
        )
    );

    yield put(actions.getAutoLines(action.payload.automationId));
    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashError(String(e)));
    return;
  }
}

export function* runAutomation(
  action: ReturnType<typeof actions.runAutomation>
  ) {
  yield put(setDashStatus({ status: "loading", error: undefined }));
  yield put(setAutomationOutput(undefined));
  
  try {
    const userState = (yield select(selectUser)) as User;
    const response = yield call(
      () =>
        apiRunAutomation(
          userState.default_organization_id,
          userState.default_workspace_id,
          action.payload.automationId,
          action.payload.selection,
          action.payload.code
        )
    );
  
    // Check if response has output field
    if (response.data && response.data.output !== undefined) {
      yield put(setAutomationOutput(response.data.output));
    }
  
    yield put(setDashStatus({ status: "idle", error: undefined }));
  } catch (e) {
    yield put(setDashStatus({ status: "idle", error: String(e) }));
    return;
  }
}